import { z } from 'zod';
import {
  CURRENCY,
  FEATURE_STATUS,
  FEATURE_TYPE,
  PART_DOC_TYPE,
  PART_LOG_SOURCE,
  PART_MOVEMENT_TYPE,
  PART_REQUEST_STATUS,
  PART_USAGE_KIND,
  PREVENTIVE_INTERVAL_TYPE,
  PREVENTIVE_STATUS,
  PRIORITY,
  SERVICE_ORDER_STATUS,
  USER_ROLE,
  VEHICLE_STATUS,
} from './constants.js';
import {
  checklistItemSchema,
  id,
  isoDate,
  isoDateTime,
  money,
  photoSchema,
  timelineEventSchema,
} from './common.js';

/* ------------------------------------------------------------------ */
/* Vehicle                                                             */
/* ------------------------------------------------------------------ */

export const vehicleSchema = z.object({
  id,
  apelido: z.string().min(1),
  marca: z.string().min(1),
  modelo: z.string().min(1),
  ano: z.number().int().min(1900).max(2100),
  chassi: z.string().optional(),
  placa: z.string().optional(),
  cor: z.string().optional(),
  kmAtual: z.number().int().nonnegative().default(0),
  status: z.enum(VEHICLE_STATUS).default('ATIVO'),
  fotoCapa: z.string().optional(),
  fotos: z.array(photoSchema).default([]),
  observacoes: z.string().optional(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Vehicle = z.infer<typeof vehicleSchema>;

export const createVehicleSchema = vehicleSchema
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial({ kmAtual: true, status: true, fotos: true });
export type CreateVehicleInput = z.infer<typeof createVehicleSchema>;

export const updateVehicleSchema = createVehicleSchema.partial();
export type UpdateVehicleInput = z.infer<typeof updateVehicleSchema>;

/* ------------------------------------------------------------------ */
/* VehicleFeature (atributo com vencimento)                            */
/* ------------------------------------------------------------------ */

export const vehicleFeatureSchema = z.object({
  id,
  vehicleId: id,
  tipo: z.enum(FEATURE_TYPE),
  label: z.string().min(1),
  fornecedor: z.string().optional(),
  dataInicio: isoDate.optional(),
  dataVencimento: isoDate,
  valor: money.optional(),
  documentoUrl: z.string().optional(),
  lembreteDiasAntes: z.number().int().nonnegative().default(30),
  status: z.enum(FEATURE_STATUS).default('ATIVO'),
  observacoes: z.string().optional(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type VehicleFeature = z.infer<typeof vehicleFeatureSchema>;

export const createVehicleFeatureSchema = vehicleFeatureSchema
  .omit({ id: true, vehicleId: true, status: true, createdAt: true, updatedAt: true })
  .partial({ lembreteDiasAntes: true });
export type CreateVehicleFeatureInput = z.infer<typeof createVehicleFeatureSchema>;

export const updateVehicleFeatureSchema = createVehicleFeatureSchema.partial();
export type UpdateVehicleFeatureInput = z.infer<typeof updateVehicleFeatureSchema>;

/* ------------------------------------------------------------------ */
/* ServiceOrder (OS)                                                   */
/* ------------------------------------------------------------------ */

export const partUsageSchema = z.object({
  partId: id,
  qtd: z.number().int().positive(),
  tipo: z.enum(PART_USAGE_KIND).default('PECA'),
});
export type PartUsage = z.infer<typeof partUsageSchema>;

/** Documento anexado a uma peça (invoice do fornecedor, nota fiscal de entrada). */
export const partDocumentSchema = z.object({
  tipo: z.enum(PART_DOC_TYPE).default('OUTRO'),
  nome: z.string().optional(),
  /** Chave S3 ou URL do arquivo (PDF ou imagem). */
  url: z.string().min(1),
  anexadoEm: isoDateTime.optional(),
});
export type PartDocument = z.infer<typeof partDocumentSchema>;

/**
 * Entrada do log de uma peça: o que mudou, quando e DE ONDE veio a informação
 * (e-mail, rastreio dos Correios/FedEx/DHL, leitura de invoice, manual…).
 * Alimenta o histórico/auditoria na tela do Pedido.
 */
export const partLogEntrySchema = z.object({
  em: isoDateTime,
  fonte: z.enum(PART_LOG_SOURCE).default('SISTEMA'),
  evento: z.string().min(1),
  /** Contexto da origem: assunto do e-mail, evento do rastreio, etc. */
  detalhe: z.string().optional(),
  /** Quem disparou (usuário/worker), quando aplicável. */
  por: z.string().optional(),
});
export type PartLogEntry = z.infer<typeof partLogEntrySchema>;

/**
 * Peça solicitada/comprada para uma OS (Solicitação de Peças).
 * Distinta de `pecasUsadas` (consumo do inventário): rastreia uma compra
 * externa — link do produto, valor + moeda, custo entregue no BR, código de
 * rastreio, print, prazo e documentos. Campos podem ser preenchidos pela IA
 * (ver `partLinkEnrich`/`trackShipment` em api.ts).
 */
export const partRequestSchema = z.object({
  id,
  titulo: z.string().optional(),
  descricao: z.string().optional(),
  /** URL do produto na loja. */
  link: z.string().optional(),
  /** Número/ID do pedido na loja (Order ID). */
  orderId: z.string().optional(),
  /** Preço do produto, na moeda `moeda`. */
  valor: money.optional(),
  /** Moeda do `valor`. */
  moeda: z.enum(CURRENCY).default('BRL'),
  /** Custo real de entrega no Brasil, em BRL (peça + frete + impostos/desembaraço). */
  valorEntregue: money.optional(),
  /** Frete do fornecedor, na moeda `moeda` (lido da invoice pela IA). */
  frete: money.optional(),
  quantidade: z.number().int().positive().default(1),
  /** Código de rastreio da encomenda. */
  codigoRastreio: z.string().optional(),
  transportadora: z.string().optional(),
  /** Foto/print do produto (chave S3 ou URL). */
  fotoUrl: z.string().optional(),
  /** Prazo/previsão de entrega (YYYY-MM-DD). */
  prazoEntrega: isoDate.optional(),
  status: z.enum(PART_REQUEST_STATUS).default('SOLICITADA'),
  /** Último evento de rastreio (ex.: "Saiu para entrega — São Paulo/SP"). */
  rastreioStatus: z.string().optional(),
  rastreioAtualizadoEm: isoDateTime.optional(),
  /** Status nativo da loja/e-commerce, como ela reporta (ex.: "Order Processed",
   * "Comprado", "Processado"). Distinto do ciclo de vida interno (status): captura
   * a granularidade da loja, inclusive o limbo "comprado mas ainda não processado". */
  lojaStatus: z.string().optional(),
  lojaStatusEm: isoDateTime.optional(),
  /** Assinatura estável do estado do rastreio (interna) p/ detectar mudança real. */
  rastreioSig: z.string().optional(),
  /** Já lançada no estoque (inventário) após entregue. */
  emEstoque: z.boolean().optional(),
  /** Id da peça de inventário criada ao lançar no estoque. */
  estoquePartId: id.optional(),
  /** Arquivada: some da visão ativa de Compras (entregues/canceladas concluídas). */
  arquivada: z.boolean().optional(),
  /** Invoice do fornecedor + nota fiscal de entrada no BR, etc. */
  documentos: z.array(partDocumentSchema).default([]),
  /** Log de proveniência: o que mudou e de qual fonte (e-mail/rastreio/manual). */
  historico: z.array(partLogEntrySchema).default([]),
  observacoes: z.string().optional(),
  criadaEm: isoDateTime,
  atualizadaEm: isoDateTime.optional(),
});
export type PartRequest = z.infer<typeof partRequestSchema>;

export const createPartRequestSchema = partRequestSchema
  .omit({ id: true, criadaEm: true, atualizadaEm: true })
  .partial({ quantidade: true, status: true, moeda: true, documentos: true, historico: true });
export type CreatePartRequestInput = z.infer<typeof createPartRequestSchema>;

export const updatePartRequestSchema = createPartRequestSchema.partial();
export type UpdatePartRequestInput = z.infer<typeof updatePartRequestSchema>;

/** Comentário livre numa OS (quem, quando, texto). */
export const comentarioSchema = z.object({
  id,
  texto: z.string().min(1),
  em: isoDateTime,
  por: z.string().optional(),
});
export type Comentario = z.infer<typeof comentarioSchema>;

export const serviceOrderSchema = z.object({
  id,
  vehicleId: id,
  titulo: z.string().min(1),
  descricao: z.string().optional(),
  status: z.enum(SERVICE_ORDER_STATUS).default('ABERTA'),
  prioridade: z.enum(PRIORITY).default('MEDIA'),
  mecanicosResponsaveis: z.array(id).default([]),
  abertaEm: isoDateTime,
  iniciadaEm: isoDateTime.optional(),
  concluidaEm: isoDateTime.optional(),
  prazo: isoDate.optional(),
  kmNaEntrada: z.number().int().nonnegative().optional(),
  checklist: z.array(checklistItemSchema).default([]),
  pecasUsadas: z.array(partUsageSchema).default([]),
  pecasSolicitadas: z.array(partRequestSchema).default([]),
  fotos: z.array(photoSchema).default([]),
  horasTrabalho: z.number().nonnegative().optional(),
  custoTotal: money.optional(),
  timeline: z.array(timelineEventSchema).default([]),
  comentarios: z.array(comentarioSchema).default([]),
  observacoes: z.string().optional(),
});
export type ServiceOrder = z.infer<typeof serviceOrderSchema>;

/** OS enriquecida com campos derivados calculados na leitura. */
export const serviceOrderWithDerivedSchema = serviceOrderSchema.extend({
  diasParado: z.number().nonnegative(),
  tempoEmAndamentoHoras: z.number().nonnegative().optional(),
});
export type ServiceOrderWithDerived = z.infer<typeof serviceOrderWithDerivedSchema>;

export const createServiceOrderSchema = serviceOrderSchema
  .omit({ id: true, iniciadaEm: true, concluidaEm: true, timeline: true })
  .partial({
    status: true,
    prioridade: true,
    mecanicosResponsaveis: true,
    checklist: true,
    pecasUsadas: true,
    pecasSolicitadas: true,
    fotos: true,
    comentarios: true,
    abertaEm: true,
  });
export type CreateServiceOrderInput = z.infer<typeof createServiceOrderSchema>;

export const updateServiceOrderSchema = createServiceOrderSchema.partial().omit({ vehicleId: true });
export type UpdateServiceOrderInput = z.infer<typeof updateServiceOrderSchema>;

export const updateServiceOrderStatusSchema = z.object({
  status: z.enum(SERVICE_ORDER_STATUS),
  nota: z.string().optional(),
});
export type UpdateServiceOrderStatusInput = z.infer<typeof updateServiceOrderStatusSchema>;

export const addCommentSchema = z.object({
  texto: z.string().min(1),
});
export type AddCommentInput = z.infer<typeof addCommentSchema>;

export const updateChecklistSchema = z.object({
  checklist: z.array(checklistItemSchema),
});
export type UpdateChecklistInput = z.infer<typeof updateChecklistSchema>;

/* ------------------------------------------------------------------ */
/* Part                                                                */
/* ------------------------------------------------------------------ */

export const partSchema = z.object({
  id,
  sku: z.string().min(1),
  nome: z.string().min(1),
  tipo: z.enum(PART_USAGE_KIND).default('PECA'),
  categoria: z.string().optional(),
  marca: z.string().optional(),
  partNumber: z.string().optional(),
  codigoBarras: z.string().optional(),
  quantidade: z.number().int().nonnegative().default(0),
  estoqueMinimo: z.number().int().nonnegative().default(0),
  localizacao: z.string().optional(),
  custoUnitario: money.optional(),
  fornecedor: z.string().optional(),
  fotoUrl: z.string().optional(),
  qrValue: z.string().min(1),
  veiculosCompativeis: z.array(z.string()).default([]),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Part = z.infer<typeof partSchema>;

export const createPartSchema = partSchema
  .omit({ id: true, qrValue: true, createdAt: true, updatedAt: true })
  .partial({ tipo: true, quantidade: true, estoqueMinimo: true, veiculosCompativeis: true });
export type CreatePartInput = z.infer<typeof createPartSchema>;

export const updatePartSchema = createPartSchema.partial();
export type UpdatePartInput = z.infer<typeof updatePartSchema>;

/* ------------------------------------------------------------------ */
/* PartMovement                                                        */
/* ------------------------------------------------------------------ */

export const partMovementSchema = z.object({
  id,
  partId: id,
  tipo: z.enum(PART_MOVEMENT_TYPE),
  quantidade: z.number().int().positive(),
  vehicleId: id.optional(),
  serviceOrderId: id.optional(),
  userId: id,
  motivo: z.string().optional(),
  saldoApos: z.number().int().nonnegative(),
  timestamp: isoDateTime,
});
export type PartMovement = z.infer<typeof partMovementSchema>;

export const createPartMovementSchema = partMovementSchema.omit({
  id: true,
  partId: true,
  saldoApos: true,
  timestamp: true,
});
export type CreatePartMovementInput = z.infer<typeof createPartMovementSchema>;

/* ------------------------------------------------------------------ */
/* PreventiveMaintenance                                               */
/* ------------------------------------------------------------------ */

export const preventiveMaintenanceSchema = z
  .object({
    id,
    vehicleId: id,
    titulo: z.string().min(1),
    tipoIntervalo: z.enum(PREVENTIVE_INTERVAL_TYPE),
    intervaloMeses: z.number().int().positive().optional(),
    intervaloKm: z.number().int().positive().optional(),
    ultimaDataFeita: isoDate.optional(),
    ultimoKmFeito: z.number().int().nonnegative().optional(),
    proximaData: isoDate.optional(),
    proximoKm: z.number().int().nonnegative().optional(),
    status: z.enum(PREVENTIVE_STATUS).default('OK'),
    lembreteDiasAntes: z.number().int().nonnegative().default(30),
    observacoes: z.string().optional(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .refine((p) => p.tipoIntervalo !== 'TEMPO' || p.intervaloMeses != null, {
    message: 'intervaloMeses é obrigatório quando tipoIntervalo inclui TEMPO',
    path: ['intervaloMeses'],
  })
  .refine((p) => p.tipoIntervalo !== 'KM' || p.intervaloKm != null, {
    message: 'intervaloKm é obrigatório quando tipoIntervalo inclui KM',
    path: ['intervaloKm'],
  });
export type PreventiveMaintenance = z.infer<typeof preventiveMaintenanceSchema>;

export const createPreventiveMaintenanceSchema = z
  .object({
    titulo: z.string().min(1),
    tipoIntervalo: z.enum(PREVENTIVE_INTERVAL_TYPE),
    intervaloMeses: z.number().int().positive().optional(),
    intervaloKm: z.number().int().positive().optional(),
    ultimaDataFeita: isoDate.optional(),
    ultimoKmFeito: z.number().int().nonnegative().optional(),
    lembreteDiasAntes: z.number().int().nonnegative().optional(),
    observacoes: z.string().optional(),
  })
  .partial({ lembreteDiasAntes: true });
export type CreatePreventiveMaintenanceInput = z.infer<typeof createPreventiveMaintenanceSchema>;

export const updatePreventiveMaintenanceSchema = createPreventiveMaintenanceSchema.partial();
export type UpdatePreventiveMaintenanceInput = z.infer<typeof updatePreventiveMaintenanceSchema>;

/* ------------------------------------------------------------------ */
/* User                                                                */
/* ------------------------------------------------------------------ */

export const userSchema = z.object({
  id,
  nome: z.string().min(1),
  email: z.string().email(),
  papel: z.enum(USER_ROLE),
  avatarUrl: z.string().optional(),
  telefone: z.string().optional(),
  ativo: z.boolean().default(true),
});
export type User = z.infer<typeof userSchema>;

export const createUserSchema = userSchema.omit({ id: true }).partial({ ativo: true });
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = createUserSchema.partial();
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
